import React, { useState, useEffect } from "react";
import _ from "lodash";
import Box from "@mui/material/Box";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import { useDispatch } from "react-redux";
import moment from "moment";

import { useOutletContext, useSearchParams } from "react-router-dom";
import axios from "axios";
import { apiUrl } from "features/configure";

import {
  Button,
  SIZE,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import {
  InputField,
  SIZE as InputSize,
} from "@metacrm/metacrm-material-ui/dist/InputField";

import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import FixedButton from "features/metadesk/settings/common/FixedButton";
import { Loading } from "features/common";
import { useIntl } from "react-intl";
import CustomDropdown from "components/CustomDropdown/CustomDropdown";
import useGetPagePermission from "hooks/useGetPagePermission";
import { useTheme } from "@mui/styles";
import { StyledWhiteContainer } from "../../common.styles";
import useNavigateEntity from "hooks/useNavigateEntity";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import Back from "features/metadesk/components/Back";

const TelegramAnalyticsBot = () => {
  const confirm = useConfirm();
  const dispatchSnackbar = useDispatchSnackbar();

  const theme = useTheme();
  const dispatch = useDispatch();
  const [entityName, isCustomDomain] = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);

  const { navigateEntity } = useNavigateEntity();
  const [tgBotData, setTgBotData] = useState({});

  const { formatMessage } = useIntl();
  const { fetchEntityConfig, entityConfig } = useFetchEntityConfig();

  const isConnected = _.get(
    entityConfig,
    "serviceModule.telegramAnalytics.connected"
  );

  const groupName = _.get(
    entityConfig,
    "serviceModule.telegramAnalytics.groupName"
  );

  useEffect(() => {
    fetchEntityConfig({ entityName });
    fetchTelegramBotInfo();
  }, []);

  const fetchTelegramBotInfo = async () => {
    try {
      const { data } = await axios.get(
        apiUrl + "/api/telegram/ticketBot/" + entityName
      );

      setTgBotData({
        created: data.created,
        name: data.name,
      });
    } catch (err) {
      if (
        !err?.response?.data?.error?.includes(
          "Telegram Ticket Bot is not setup correctly for"
        )
      ) {
        dispatchSnackbar({ err });
      }
    }
  };

  const disconnectFunction = async () => {
    try {
      setIsLoading(true);
      await axios.post(
        `${apiUrl}/api/telegram/userBot/${entityName}/disconnect`
      );
      fetchEntityConfig({ entityName });
    } catch (error) {
      dispatchSnackbar({ err: error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    confirm({
      title: formatMessage({
        id: "telegram.disconnect.confirm.title",
      }),
      content: formatMessage({
        id: "telegram.disconnect.confirm.description",
      }),
      confirmText: formatMessage({
        id: "global.btn.confirm",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(() => {
        disconnectFunction();
      })
      .catch(() => {});
  };

  const handleToIntegration = () => {
    navigateEntity("/settings/customization/telegramAnalyticsSetting");
  };

  const pagePermission = useGetPagePermission();
  const integrationPermission = pagePermission("integration");

  const renderInstallPrompt = () => (
    <Box
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      maxWidth={"432px"}
      margin={"0 auto"}
      flexDirection={"column"}
      marginTop={"5%"}
      className="fade-up-more"
    >
      <Box
        component="img"
        width={"100%"}
        src={require(`assets/img/not_install_telegram_bot.svg`).default}
        mb={3}
      ></Box>

      <Box
        fontSize={"24px"}
        fontWeight={"700"}
        color={(theme) => theme.customColors.grey[800]}
        textAlign={"center"}
        mb="5px"
      >
        {formatMessage({
          id: "integration.telegramAnalyticsBot.installPrompt.title",
        })}
      </Box>

      <Box
        color={(theme) => theme.customColors.grey[600]}
        textAlign={"center"}
        mb={3}
        fontWeight={400}
        lineHeight={"20px"}
      >
        {formatMessage({
          id: "integration.telegramAnalyticsBot.installPrompt.description",
        })}
      </Box>
      {integrationPermission.canEdit && (
        <Button onClick={handleToIntegration}>
          {formatMessage({ id: "global.btn.integrate" })}
        </Button>
      )}
    </Box>
  );

  return (
    <>
      <div className="fade-up">
        <Back />
        <Box mb="20px" display={"flex"} justifyContent={"space-between"}>
          <Box className="title">
            {formatMessage({ id: "integration.telegramAnalyticsBot.title" })}
          </Box>
          {isConnected && (
            <Button onClick={handleDisconnect}>
              {formatMessage({ id: "global.btn.disconnect" })}
            </Button>
          )}
        </Box>
      </div>
      {isConnected ? (
        <StyledWhiteContainer
          sx={{ marginBottom: "20px" }}
          className="fade-up-more"
        >
          <Grid container spacing={3}>
            <Grid item xs={4}>
              {formatMessage({ id: "dAppTracking.instance.labelName" })}:
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  backgroundColor: theme.customColors.grey[200],
                  color: theme.customColors.grey[500],
                  padding: "12px",
                  borderRadius: "3px",
                  marginBottom: "4px",
                }}
              >
                {groupName}
              </Box>

              <Box
                display={"flex"}
                sx={{ fontSize: "12px", alignItems: "center" }}
              >
                <Box color={theme.customColors.grey[600]}>
                  {formatMessage({ id: "global.header.createdTime" })} :
                </Box>
                <Box ml={"5px"} color={theme.customColors.purple[500]}>
                  {moment(tgBotData.created).format("MMM DD,YYYY HH:mmA")}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={2}></Grid>
          </Grid>
        </StyledWhiteContainer>
      ) : (
        renderInstallPrompt()
      )}
    </>
  );
};

export default TelegramAnalyticsBot;
